import SensorList from "./SensorList";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SensorDetail from "./SensorDetail";
import SensorForm from "./Form/SensorForm";
import SensorLookup from "./SensorLookup";
import NoSelected from "@components/NoSelected";
import { useTranslation } from "react-i18next";
import Spinner from "@components/Spinner/Spinner";
import { Container, Row, Col, Offcanvas } from "react-bootstrap";
import { getAllSensors } from "../services/sensorCrud";
import ServerError from "@components/Error/ServerError";
import SensorMobileListView from "./SensorMobileListView";
import MobileBreadcrumb from "@components/Breadcrumb/MobileBreadcrumb";
import EmptySensors from "./EmptySensors";
import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";
import useWindowDimensions from "@hooks/useWindowDimension";

export default function SensorPage() {
  const { mobile } = useSelector((state) => state.mobile);
  const [isUpdate, setIsUpdate] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [detail, setDetail] = useState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const id = query.get("selectedSensor");
  const { width } = useWindowDimensions();
  const useRightDrawer = width < 992;

  // Determine if we're on lookup page
  const isLookupPage = location.pathname.includes("/sensors/lookup");

  // Fetching sensor list with pagination and search (only for list page, not lookup)
  const scheduledGetAllSensors = createScheduledQueryFn(
    () => {
      return getAllSensors({ page, pageSize, search });
    },
    REQUEST_PRIORITIES.HIGH,
    "getAllSensors"
  );
  const {
    data: sensorResponse,
    isError,
    isLoading,
  } = useQuery(["sensors", { page, pageSize, search }], scheduledGetAllSensors, {
    enabled: !isLookupPage, // Only fetch for list page
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    keepPreviousData: true, // Keep previous page while fetching new one
  });

  const sensorData = sensorResponse?.results || [];
  const totalCount = sensorResponse?.count || 0;
  const hasNext = !!sensorResponse?.next;
  const hasPrevious = !!sensorResponse?.previous;

  const closeSensorDrawer = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selectedSensor");
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`);
  };

  //delete sensor function
  if (!isLookupPage && isLoading && !sensorResponse) return <Spinner height={"80vh"} />;
  if (!isLookupPage && isError) return <ServerError />;

  return (
    <>
      {/* mobile */}

      {mobile ? (
        <>
          <MobileBreadcrumb />
          <Container>
            {isLookupPage ? (
              <SensorLookup />
            ) : totalCount === 0 && !search ? (
              <EmptySensors />
            ) : (
              <SensorMobileListView
                sensors={sensorData}
                sensorId={id}
                setIsUpdate={setIsUpdate}
                setDetail={setDetail}
                setOpenForm={setOpenForm}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                search={search}
                setSearch={setSearch}
                totalCount={totalCount}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                isLoading={isLoading}
              />
            )}
          </Container>
        </>
      ) : (
        <>
          <Container className="entity-list-page">
            {isLookupPage ? (
              <SensorLookup />
            ) : totalCount === 0 && !search ? (
              <EmptySensors />
            ) : (
              <Row className="g-3">
                <Col lg={useRightDrawer ? 12 : id ? 7 : 12}>
                  <SensorList
                    sensors={sensorData}
                    setDetail={setDetail}
                    setIsUpdate={setIsUpdate}
                    setOpenForm={setOpenForm}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    search={search}
                    setSearch={setSearch}
                    totalCount={totalCount}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    isLoading={isLoading}
                  />
                </Col>
                {id && !useRightDrawer && (
                  <Col lg="5">
                    {!id ? (
                      <NoSelected text={t("sensor_details.no_sensor_selected")} />
                    ) : (
                      <SensorDetail setIsUpdate={setIsUpdate} setOpenForm={setOpenForm} />
                    )}
                  </Col>
                )}
              </Row>
            )}
          </Container>
        </>
      )}
      {useRightDrawer && (
        <Offcanvas
          show={Boolean(id)}
          onHide={closeSensorDrawer}
          placement="end"
          backdrop={false}
          scroll={false}
          style={{
            width: "min(92vw, 560px)",
            top: "4rem",
            height: "calc(100dvh - 4rem)",
            zIndex: 1020,
          }}
        >
          <Offcanvas.Body className="p-0">
            <SensorDetail setIsUpdate={setIsUpdate} setOpenForm={setOpenForm} />
          </Offcanvas.Body>
        </Offcanvas>
      )}
      <SensorForm
        isUpdate={isUpdate}
        detail={detail}
        setIsUpdate={setIsUpdate}
        show={openForm}
        setOpenForm={setOpenForm}
      />
    </>
  );
}
